import { ReactNode, useEffect, useState, useRef } from "react";
import {
  ComboBoxOptionType,
  DiscreteInput,
  DiscreteOption,
} from "@/components/RightStyle/TailwindStylingPanel/components/input";
import { useTailwindProperties } from "@/hooks/useTailwindProperties";
import { safeParseCssStringAsInlineStyles } from "../../utils";
import useGradient, { GradientState } from "../../hooks/useGradient";
import { TempoElement } from "@/utils/tempoElement";
import {
  ArrowBottomLeftIcon,
  ArrowBottomRightIcon,
  ArrowTopLeftIcon,
  ArrowTopRightIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
} from "@radix-ui/react-icons";
import React from "react";
import CustomColorPicker from "../input/CustomColorPicker";
import CustomImageUpload from "../input/CustomImageUpload";
import CustomGradientInput from "../input/CustomGradientInput";
import { PropertyType } from "@/tailwind/tailwindUtils";
import { useEditingClassesByCodebaseId } from "@/store/zustand/designCommandManagerStore";
import { JsxElement } from "@/utils/jsxElementTypes";
import { useNewPropertyState2 } from "@/components/RightStyle/usePropertyStateV2";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { noop } from "lodash";
import { VariantControl } from "../variant/VariantControl";

interface Props {
  selectedElement?: TempoElement | null;
  selectedTreeElement?: JsxElement | null;
  pickTailwindClassForProperty: (args: {
    option: ComboBoxOptionType;
    propertyType: PropertyType;
    setTemporaryDraggingValue?: (value: string | null) => void;
    variant?: string | null;
  }) => void;
  updateInstantForProperty: (args: {
    propertyType: PropertyType;
    option: ComboBoxOptionType;
    setTemporaryDraggingValue?: (value: string | null) => void;
  }) => void;
  removeClassForProperty: (args: { propertyType: PropertyType }) => void;
  cssValues: { [key: string]: any };
  onOpenFillVariantModal?: () => void;
  fillVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  modalPopoverRef?: React.RefObject<HTMLDivElement>;
  onOpenFromVariantModal?: () => void;
  fromVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  onOpenViaVariantModal?: () => void;
  viaVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
  onOpenToVariantModal?: () => void;
  toVariantAnchorRef?: React.RefObject<HTMLButtonElement>;
}

enum ViewType {
  Fill = "fill",
  Gradient = "gradient",
  Image = "image",
}

const viewOptions: DiscreteOption[] = [
  {
    key: ViewType.Fill,
    option: {
      tailwind: "",
      css: "",
    },
    labelContent: (
      <div className="text-xs font-semibold h-6 flex items-center">Solid</div>
    ),
  },
  {
    key: ViewType.Gradient,
    // @ts-expect-error TS2322
    option: null,
    labelContent: (
      <div className="text-xs font-semibold h-6 flex items-center">
        Gradient
      </div>
    ),
  },
  {
    key: ViewType.Image,
    // @ts-expect-error TS2322
    option: null,
    labelContent: (
      <div className="text-xs font-semibold h-6 flex items-center">Image</div>
    ),
  },
];

enum BackgroundImageType {
  None = "none",
  Gradient = "gradient",
  Image = "image",
}

const FillModal: React.FC<Props> = ({
  selectedTreeElement,
  pickTailwindClassForProperty,
  updateInstantForProperty,
  removeClassForProperty,
  cssValues,
  selectedElement,
  onOpenFillVariantModal,
  fillVariantAnchorRef,
  modalPopoverRef,
  onOpenFromVariantModal,
  fromVariantAnchorRef,
  onOpenViaVariantModal,
  viaVariantAnchorRef,
  onOpenToVariantModal,
  toVariantAnchorRef,
}) => {
  const { classes: elementClasses } = useEditingClassesByCodebaseId(
    selectedTreeElement || null
  );

  const tailwindClassesMap = useCodebaseStore(
    (state) => state.tailwindClassesMap
  );
  const [view, setView] = useState<ViewType>(() => {
    const backgroundImageValue = cssValues["background-image"];
    if (
      backgroundImageValue?.evaluatedValue?.includes("gradient") ||
      backgroundImageValue?.value?.includes("gradient")
    ) {
      return ViewType.Gradient;
    } else if (
      backgroundImageValue?.evaluatedValue?.includes("url") ||
      backgroundImageValue?.value?.includes("url")
    ) {
      return ViewType.Image;
    } else {
      return ViewType.Fill;
    }
  });

  const backgroundColorProperties = useTailwindProperties(
    PropertyType.BACKGROUND_COLOR
  );

  const backgroundColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BACKGROUND_COLOR,
    cssValues,
  });

  const fromColorProperties = useTailwindProperties(PropertyType.FROM_COLOR);

  const backgroundImageState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.BACKGROUND_IMAGE,
    cssValues,
  });

  const toColorProperties = useTailwindProperties(PropertyType.TO_COLOR);
  const toColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TO_COLOR,
    cssValues,
  });

  const viaColorProperties = useTailwindProperties(PropertyType.VIA_COLOR);
  const viaColorState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.VIA_COLOR,
    cssValues,
  });

  const fromPositionProperties = useTailwindProperties(
    PropertyType.FROM_POSITION
  );
  const viaPositionProperties = useTailwindProperties(
    PropertyType.VIA_POSITION
  );
  const toPositionProperties = useTailwindProperties(PropertyType.TO_POSITION);

  const fromPositionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.FROM_POSITION,
    cssValues,
  });
  const viaPositionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.VIA_POSITION,
    cssValues,
  });
  const toPositionState = useNewPropertyState2({
    elementClasses,
    tailwindClassMap: tailwindClassesMap,
    propertyType: PropertyType.TO_POSITION,
    cssValues,
  });

  const [fromPositionVariantModalOpen, setFromPositionVariantModalOpen] =
    useState(false);
  const fromPositionVariantAnchorRef = useRef<HTMLButtonElement>(null);
  const [viaPositionVariantModalOpen, setViaPositionVariantModalOpen] =
    useState(false);
  const viaPositionVariantAnchorRef = useRef<HTMLButtonElement>(null);
  const [toPositionVariantModalOpen, setToPositionVariantModalOpen] =
    useState(false);
  const toPositionVariantAnchorRef = useRef<HTMLButtonElement>(null);

  const buildBackgroundColorPreview = (
    option: ComboBoxOptionType
  ): ReactNode => {
    const styles = safeParseCssStringAsInlineStyles(option.css);
    return <div className={`w-full h-full`} style={styles}></div>;
  };

  const computedGradient = useGradient(
    cssValues["background-image"]?.evaluatedValue,
    elementClasses,
    selectedElement
  );

  const backgroundImageProperties = useTailwindProperties(
    PropertyType.BACKGROUND_IMAGE
  );

  const [gradientDirectionIcon, setGradientDirectionIcon] = useState(
    <ArrowRightIcon />
  );

  const [gradient, setGradient] = useState<GradientState>({
    // @ts-expect-error TS2322
    direction: null,
    from: {
      color: {
        value: "#FFFFFF",
        classname: undefined,
      },
      set: false,
    },
  });

  useEffect(() => {
    if (computedGradient) {
      setGradient(computedGradient);

      switch (computedGradient.direction) {
        case "to top":
          setGradientDirectionIcon(<ArrowUpIcon />);
          break;
        case "to right":
          setGradientDirectionIcon(<ArrowRightIcon />);
          break;
        case "to bottom":
          setGradientDirectionIcon(<ArrowDownIcon />);
          break;
        case "to left":
          setGradientDirectionIcon(<ArrowLeftIcon />);
          break;
        case "to top right":
        case "to right top":
          setGradientDirectionIcon(<ArrowTopRightIcon />);
          break;
        case "to top left":
        case "to left top":
          setGradientDirectionIcon(<ArrowTopLeftIcon />);
          break;
        case "to bottom right":
        case "to right bottom":
          setGradientDirectionIcon(<ArrowBottomRightIcon />);
          break;
        case "to bottom left":
        case "to left bottom":
          setGradientDirectionIcon(<ArrowBottomLeftIcon />);
          break;
        default:
          setGradientDirectionIcon(<ArrowRightIcon />);
      }
    } else {
      setGradient({
        // @ts-expect-error TS2322
        direction: null,
        from: {
          color: {
            value: "#FFFFFF",
            classname: undefined,
          },
          set: false,
        },
      });
    }
  }, [computedGradient]);

  return (
    <div
      className="grid grid-cols-14 items-center gap-x-3 gap-y-2"
      ref={modalPopoverRef}
    >
      <div className="col-span-14">
        <DiscreteInput
          className="p-0.5"
          options={viewOptions}
          onOptionSelected={(option) => {
            setView(option.key as ViewType);
          }}
          selected={view}
          set={false}
        />
      </div>
      {view === ViewType.Fill && (
        <>
          <div className="col-span-14" style={{ position: "relative" }}>
            <CustomColorPicker
              propertyState={backgroundColorState}
              buildArbitraryClass={(input: string) => {
                return {
                  tailwind: `bg-[${input}]`,
                  css: `background-color: ${input}`,
                };
              }}
              instantUpdateValue={(option) =>
                updateInstantForProperty({
                  propertyType: PropertyType.BACKGROUND_COLOR,
                  option,
                  setTemporaryDraggingValue:
                    backgroundColorState.setTemporaryDraggingValue,
                })
              }
              comboboxOptions={backgroundColorProperties}
              setValue={(option) =>
                pickTailwindClassForProperty({
                  option,
                  propertyType: PropertyType.BACKGROUND_COLOR,
                  setTemporaryDraggingValue:
                    backgroundColorState.setTemporaryDraggingValue,
                })
              }
              buildOptionPreview={buildBackgroundColorPreview}
              tooltipContent="Background color"
              onClear={(variant?: string) => {
                removeClassForProperty({
                  propertyType: PropertyType.BACKGROUND_COLOR,
                });
              }}
              showHex={true}
              onOpenVariantModal={onOpenFillVariantModal}
            />
            <button
              ref={fillVariantAnchorRef}
              style={{
                position: "absolute",
                right: 0,
                top: 0,
                width: 1,
                height: 1,
                opacity: 0,
                pointerEvents: "none",
              }}
              tabIndex={-1}
              aria-hidden
            />
          </div>
        </>
      )}
      {view === ViewType.Gradient && (
        <>
          <CustomGradientInput
            backgroundImageState={backgroundImageState}
            backgroundImageProperties={backgroundImageProperties}
            gradientDirectionIcon={gradientDirectionIcon}
            setValue={(option) => {
              if (option.tailwind.startsWith("from-")) {
                pickTailwindClassForProperty({
                  option,
                  propertyType: PropertyType.FROM_COLOR,
                  setTemporaryDraggingValue:
                    backgroundImageState.setTemporaryDraggingValue,
                });
              } else if (option.tailwind.startsWith("to-")) {
                pickTailwindClassForProperty({
                  option,
                  propertyType: PropertyType.TO_COLOR,
                  setTemporaryDraggingValue:
                    backgroundImageState.setTemporaryDraggingValue,
                });
              } else if (option.tailwind.startsWith("via-")) {
                pickTailwindClassForProperty({
                  option,
                  propertyType: PropertyType.VIA_COLOR,
                  setTemporaryDraggingValue:
                    backgroundImageState.setTemporaryDraggingValue,
                });
              } else if (
                option.tailwind.startsWith("bg-gradient-") ||
                option.tailwind.startsWith("bg-[url(")
              ) {
                pickTailwindClassForProperty({
                  option,
                  propertyType: PropertyType.BACKGROUND_IMAGE,
                  setTemporaryDraggingValue:
                    backgroundImageState.setTemporaryDraggingValue,
                });
              }
            }}
            removeTailwindClass={(option) =>
              removeClassForProperty({
                propertyType: PropertyType.BACKGROUND_IMAGE,
              })
            }
            classList={elementClasses}
            evaluatedValue={gradient?.from?.color?.value || "#fffff00"}
            set={Boolean(gradient?.from?.set)}
            gradient={gradient}
            setGradient={setGradient}
            setSet={() => {}}
            instantUpdateValue={(option) =>
              updateInstantForProperty({
                propertyType: PropertyType.BACKGROUND_IMAGE,
                option,
                setTemporaryDraggingValue:
                  backgroundImageState.setTemporaryDraggingValue,
              })
            }
            fromColorProperties={fromColorProperties}
            toColorProperties={toColorProperties}
            viaColorProperties={viaColorProperties}
            toPositionProperties={toPositionProperties}
            viaPositionProperties={viaPositionProperties}
            fromPositionProperties={fromPositionProperties}
            backgroundColorProperties={backgroundColorProperties}
            buildArbitraryClass={(
              currentColor: "from" | "to" | "via",
              input: string
            ) => {
              switch (currentColor) {
                case "from":
                  return {
                    tailwind: `from-[${input}]`,
                    css: `--tw-gradient-from: ${input} var(--tw-gradient-from-position);`,
                  };
                case "to":
                  return {
                    tailwind: `to-[${input}]`,
                    css: `--tw-gradient-to: ${input} var(--tw-gradient-to-position);`,
                  };
                case "via":
                  return {
                    tailwind: `via-[${input}]`,
                    css: `--tw-gradient-stops: var(--tw-gradient-from), ${input} var(--tw-gradient-via-position), var(--tw-gradient-to);`,
                  };
              }
            }}
            onClear={(variant?: string) => {
              if (gradient?.from?.color?.classname) {
                removeClassForProperty({
                  propertyType: PropertyType.BACKGROUND_IMAGE,
                });
              }
            }}
            onOpenFromVariantModal={onOpenFromVariantModal}
            fromVariantAnchorRef={fromVariantAnchorRef}
            onOpenViaVariantModal={onOpenViaVariantModal}
            viaVariantAnchorRef={viaVariantAnchorRef}
            onOpenToVariantModal={onOpenToVariantModal}
            toVariantAnchorRef={toVariantAnchorRef}
            pickTailwindClassForProperty={pickTailwindClassForProperty}
            fromPositionState={fromPositionState}
            viaPositionState={viaPositionState}
            toPositionState={toPositionState}
          />
        </>
      )}
      {view === ViewType.Image && (
        <>
          <CustomImageUpload
            value={backgroundImageState.propertyValue}
            evaluatedValue={backgroundImageState.propertyValue}
            setValue={(val) => {
              backgroundImageState.setTemporaryDraggingValue(val);
            }}
            set={backgroundImageState.isSet}
            setSet={noop}
            buildOptionPreview={buildBackgroundColorPreview}
            buildArbitraryClass={(input: string) => {
              const url = `url('${input}')`;
              return {
                tailwind: `bg-[${url}]`,
                css: `background-image: ${url}`,
              };
            }}
            onComboboxSelected={(option) => {
              pickTailwindClassForProperty({
                option,
                propertyType: PropertyType.BACKGROUND_IMAGE,
                setTemporaryDraggingValue:
                  backgroundImageState.setTemporaryDraggingValue,
              });
            }}
            onClear={(variant?: string) => {
              removeClassForProperty({
                propertyType: PropertyType.BACKGROUND_IMAGE,
              });
            }}
          />
        </>
      )}
      {fromPositionVariantModalOpen && (
        <VariantControl
          valueByVariant={fromPositionState.valueByVariant}
          onVariantChange={(variant, value) => {
            pickTailwindClassForProperty({
              option: {
                tailwind: `from-[${value}]`,
                css: `--tw-gradient-from-position: ${value}`,
              },
              propertyType: PropertyType.FROM_POSITION,
              setTemporaryDraggingValue:
                fromPositionState.setTemporaryDraggingValue,
              variant,
            });
            setFromPositionVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            pickTailwindClassForProperty({
              option: { tailwind: "", css: "" },
              propertyType: PropertyType.FROM_POSITION,
              setTemporaryDraggingValue:
                fromPositionState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="direction-wrap"
          anchorRef={fromPositionVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={fromPositionVariantModalOpen}
          setModalOpen={setFromPositionVariantModalOpen}
        >
          {() => null}
        </VariantControl>
      )}
      {viaPositionVariantModalOpen && (
        <VariantControl
          valueByVariant={viaPositionState.valueByVariant}
          onVariantChange={(variant, value) => {
            pickTailwindClassForProperty({
              option: {
                tailwind: `via-[${value}]`,
                css: `--tw-gradient-via-position: ${value}`,
              },
              propertyType: PropertyType.VIA_POSITION,
              setTemporaryDraggingValue:
                viaPositionState.setTemporaryDraggingValue,
              variant,
            });
            setViaPositionVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            pickTailwindClassForProperty({
              option: { tailwind: "", css: "" },
              propertyType: PropertyType.VIA_POSITION,
              setTemporaryDraggingValue:
                viaPositionState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="direction-wrap"
          anchorRef={viaPositionVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={viaPositionVariantModalOpen}
          setModalOpen={setViaPositionVariantModalOpen}
        >
          {() => null}
        </VariantControl>
      )}
      {toPositionVariantModalOpen && (
        <VariantControl
          valueByVariant={toPositionState.valueByVariant}
          onVariantChange={(variant, value) => {
            pickTailwindClassForProperty({
              option: {
                tailwind: `to-[${value}]`,
                css: `--tw-gradient-to-position: ${value}`,
              },
              propertyType: PropertyType.TO_POSITION,
              setTemporaryDraggingValue:
                toPositionState.setTemporaryDraggingValue,
              variant,
            });
            setToPositionVariantModalOpen(false);
          }}
          onClearVariant={(variant) => {
            pickTailwindClassForProperty({
              option: { tailwind: "", css: "" },
              propertyType: PropertyType.TO_POSITION,
              setTemporaryDraggingValue:
                toPositionState.setTemporaryDraggingValue,
              variant,
            });
          }}
          controlType="direction-wrap"
          anchorRef={toPositionVariantAnchorRef}
          currentFlexDirection=""
          currentJustifyContent=""
          currentAlignItems=""
          modalOpen={toPositionVariantModalOpen}
          setModalOpen={setToPositionVariantModalOpen}
        >
          {() => null}
        </VariantControl>
      )}
    </div>
  );
};

export default React.memo(FillModal);
